import type { FC } from "react";
import { useCallback, useMemo, useRef } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { TelephoneFieldValue } from "@jti/commerce-types";
import { Controller, useFormContext } from "@jti/ui";

import { type ShouldUseProfileDataFieldConfig, telephoneFieldFactory, ToggleField } from "@checkout-v2/formFields";

export type RecipientToggleProps = {
  fieldConfig: ShouldUseProfileDataFieldConfig;
  firstNameSavedDefault: string;
  lastNameSavedDefault: string;
  telephoneSavedDefault: TelephoneFieldValue;
};

export const RecipientToggle: FC<RecipientToggleProps> = ({
  fieldConfig,
  firstNameSavedDefault,
  lastNameSavedDefault,
  telephoneSavedDefault,
}) => {
  const { control, setValue, getValues, clearErrors } = useFormContext();
  const { isInternationalPhoneEnabled, TELEPHONE_CODES_LIST } = useConfig();

  const emptyTelephone = useMemo(
    () => telephoneFieldFactory.toEmptyTelephoneField(isInternationalPhoneEnabled, TELEPHONE_CODES_LIST),
    [isInternationalPhoneEnabled, TELEPHONE_CODES_LIST]
  );

  const localValues = useRef<{
    firstName: string;
    lastName: string;
    telephone: TelephoneFieldValue;
  } | null>(null);

  const onToggle = useCallback(
    (isForSomeoneElse: boolean) => {
      const shouldUseProfileData = !isForSomeoneElse;
      clearErrors();
      setValue(fieldConfig.name, shouldUseProfileData);

      if (isForSomeoneElse && !localValues.current) {
        // Toggled ON for the first time — clear fields for fresh input
        setValue("firstName", "");
        setValue("lastName", "");
        setValue("telephone", emptyTelephone);
      } else if (!isForSomeoneElse) {
        // Toggled OFF — save current recipient inputs and restore profile defaults
        localValues.current = {
          firstName: getValues("firstName"),
          lastName: getValues("lastName"),
          telephone: getValues("telephone"),
        };
        setValue("firstName", firstNameSavedDefault);
        setValue("lastName", lastNameSavedDefault);
        setValue("telephone", telephoneSavedDefault);
      } else if (localValues.current) {
        // Toggled ON again — restore previously entered recipient data
        setValue("firstName", localValues.current.firstName);
        setValue("lastName", localValues.current.lastName);
        setValue("telephone", localValues.current.telephone);
      }
    },
    [
      setValue,
      getValues,
      clearErrors,
      fieldConfig.name,
      firstNameSavedDefault,
      lastNameSavedDefault,
      telephoneSavedDefault,
      emptyTelephone,
    ]
  );

  return (
    <Controller
      control={control}
      name={fieldConfig.name}
      render={({ field }) => {
        const isForSomeoneElse = !field.value;

        return (
          <ToggleField
            fieldConfig={fieldConfig}
            value={isForSomeoneElse}
            dataTestId="shouldUseProfileDataToggle"
            onChange={onToggle}
          >
            {isForSomeoneElse ? fieldConfig.toggleYesLabel : fieldConfig.toggleNoLabel}
          </ToggleField>
        );
      }}
    />
  );
};
